/**
 * The recipient and subject rules of a case's Send Email, mirroring
 * cynque-api-server's CaseReplyForPost validators (issue #1666,
 * resources/api/cases/request_handlers.py) so a problem shows while the user
 * is still composing rather than as a 400 after Send. Shared by the form's
 * validation and the MSW mock, so the two cannot drift.
 */

// Postmark's per-message ceiling, across To, Cc and Bcc together.
export const MAX_RECIPIENTS = 50

// RFC 5321's path limit, less the angle brackets.
const MAX_ADDRESS_LENGTH = 254

// A bare addr-spec: no display name, and none of the characters that separate
// or quote addresses in a header. Postmark takes To, Cc and Bcc as comma-joined
// strings, so ONE entry containing a comma is two recipients nobody counted or
// checked -- excluding those characters is the server's security property; the
// rest (one `@`, a dotted domain) only catches a typo early. Same pattern as the
// server's `_ADDRESS`.
// eslint-disable-next-line no-control-regex
const ADDRESS_PART = String.raw`[^\s\x00-\x1f\x7f@,;<>"()\[\]\\]+`
// eslint-disable-next-line no-control-regex
const LAST_LABEL = String.raw`[^\s\x00-\x1f\x7f@,;<>"()\[\]\\.]+`
const ADDRESS = new RegExp(`^${ADDRESS_PART}@${ADDRESS_PART}\\.${LAST_LABEL}$`)

export function isBareEmailAddress(address: string): boolean {
  return address.length <= MAX_ADDRESS_LENGTH && ADDRESS.test(address)
}

/**
 * Addresses named more than once across the groups, compared
 * case-insensitively. A duplicate would leave the message short of
 * `delivered` forever: the delivery roll-up counts recipients, and the extra
 * one has no delivery event to match.
 */
export function duplicateRecipients(...groups: string[][]): string[] {
  const seen = new Set<string>()
  const duplicates: string[] = []
  for (const address of groups.flat()) {
    const key = address.toLowerCase()
    if (seen.has(key) && !duplicates.includes(key)) {
      duplicates.push(key)
    }
    seen.add(key)
  }
  return duplicates
}

/**
 * The subject problem, or true. A header value spans one line: a CR or LF is
 * at best a mangled subject and at worst a header injection.
 */
export function subjectProblem(subject: string): true | string {
  if (!subject.trim()) {
    return 'Subject can not be blank.'
  }
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(subject)) {
    return 'Subject must be a single line.'
  }
  return true
}
